import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CanonicalLink from "./components/CanonicalLink";
import ScrollToTop from "./components/ScrollToTop";
import { LanguageProvider } from "./contexts/LanguageContext";
import Index from "./pages/Index";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import ProductNotFound from "./pages/ProductNotFound";
import Industries from "./pages/Industries";
import IndustryDetail from "./pages/IndustryDetail";
import Contacts from "./pages/Contacts";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Helmet>
        <title>KM KIT - Industrial Components & Spare Parts</title>
        <meta
          name="description"
          content="KM KIT supplies industrial components, spare parts and complete solutions for production facilities. Reliable supply for critical industries."
        />
        <meta property="og:title" content="KM KIT - Industrial Components & Spare Parts" />
        <meta
          property="og:description"
          content="Reliable supply of industrial components for critical industries. Professional support. Fast response."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
          {/* Redirect root to default language */}
          <Route path="/" element={<Navigate to="/en" replace />} />

          {/* Language-prefixed routes */}
          <Route path="/:lang/*" element={<LanguageWrapper />} />
          
          {/* Catch-all for non-language routes or invalid paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

const LanguageWrapper = () => {
  const { lang } = useParams<{ lang?: string }>();

  return (
    <LanguageProvider key={lang}>
      <CanonicalLink />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:productId" element={<ProductDetail />} />
        <Route path="/products/not-found" element={<ProductNotFound />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/industries/:slug" element={<IndustryDetail />} />
        <Route path="/contacts" element={<Contacts />} />
      </Routes>
    </LanguageProvider>
  );
};

export default App;